// shared/analytics/tracker.ts
/**
 * First-party tracker, bundled into BOTH apps (no separate <script>):
 *   la-mirage.in → client-lamirage-in/src/main.tsx (every navigation is a full load: 1 load = 1 pageview)
 *   lamirage.co  → client/src/hooks/useFirstPartyAnalytics.ts (SPA: pageview per wouter location change)
 * Rules (spec §1.1, §6): GPC/DNT ⇒ nothing sent, nothing stored. Route TEMPLATES only, no titles.
 * sendBeacon('/api/pulse', text) — same-origin on every host; with a portal token, fetch+keepalive
 * with Authorization instead. ≤2 KB. Every exported function swallows every error.
 */
import type { EventName } from './enums';
import { EVENT_PROP_KEYS, type EventPropsMap } from './events';
import { pathTemplate } from './pathTemplate';
import * as core from './trackerCore';

export interface InitOptions {
  app: 'lamirage-in' | 'lamirage-co';
  getToken?: () => string | null;
  notFound?: () => boolean;
}

const S = {
  on: false, opts: null as InitOptions | null, landing: null as core.Landing | null, firstPv: true, ret: false,
  path: '/', rawPath: '/', visibleMs: 0, visibleSince: 0, maxScroll: 0,
  wid: null as number | null, widRawPath: null as string | null, nf: false,
};

/**
 * Native app shells never report: Capacitor serves the bundle from https://localhost (Android,
 * no port) or capacitor://localhost (iOS); a relative /api/pulse there hits the app's own local
 * server. Dev (http://localhost:5000) has a port and still runs.
 */
function isWebPage(): boolean {
  const l = window.location;
  return /^https?:$/.test(l.protocol) && !(l.hostname === 'localhost' && !l.port);
}

function guard(f: () => void): void { try { f(); } catch { /* analytics never throws into the app */ } }
function kv(kind: 'local' | 'session'): core.KV | null {
  try { return kind === 'local' ? window.localStorage : window.sessionStorage; } catch { return null; }
}
function randId(): string {
  try {
    const b = new Uint8Array(8);
    crypto.getRandomValues(b);
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  } catch {
    return (Math.random().toString(16).slice(2, 10) + Math.random().toString(16).slice(2, 10)).padEnd(16, '0');
  }
}
const visible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';

function send(body: Record<string, unknown>): void {
  guard(() => {
    const text = JSON.stringify(body);
    if (text.length > 2048) return;
    const token = S.opts?.getToken?.() ?? null;
    if (!token && typeof navigator.sendBeacon === 'function' && navigator.sendBeacon('/api/pulse', text)) return;
    void fetch('/api/pulse', {
      method: 'POST', keepalive: true, credentials: 'omit', body: text,
      headers: { 'Content-Type': 'text/plain', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    }).catch(() => undefined);
  });
}

function touchSession(): { id: string; isNew: boolean } {
  const ss = kv('session');
  return ss ? core.touchSession(ss, Date.now(), randId) : { id: randId(), isNew: S.firstPv };
}

function onScroll(): void {
  guard(() => {
    const h = Math.max(1, document.documentElement.scrollHeight);
    S.maxScroll = Math.max(S.maxScroll, Math.min(100, ((window.scrollY + window.innerHeight) / h) * 100));
  });
}
function onVisibility(): void {
  guard(() => { if (document.visibilityState === 'hidden') flushEngagement(); else S.visibleSince = Date.now(); });
}
function onClick(e: Event): void {
  guard(() => {
    const a = (e.target as Element | null)?.closest?.('a[href]');
    const n = a ? core.clickEventFor(a.getAttribute('href') ?? '') : null;
    if (n) trackEvent(n, { p: S.path });
  });
}

export function initAnalytics(opts: InitOptions): void {
  guard(() => {
    if (S.on || typeof window === 'undefined' || !isWebPage()) return;
    if (core.isOptedOut(navigator as never, window as never)) return;
    S.on = true;
    S.opts = opts;
    const u = core.readUtm(window.location.search);
    S.landing = { referrerHost: core.referrerHostOf(document.referrer), utm: u.utm, clickKind: u.clickKind, template: pathTemplate(window.location.pathname) };
    const ls = kv('local');
    S.ret = ls ? core.markSeen(ls, new Date()) : false;
    S.visibleSince = visible() ? Date.now() : 0;
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', () => flushEngagement());
    document.addEventListener('click', onClick, true);
    (window as unknown as { lmAnalytics: unknown }).lmAnalytics = {
      event: (n: string, p?: Record<string, unknown>) => trackEvent(n as EventName, (p ?? {}) as never),
      // R2: the support-widget loader reads the note HERE (expiry + opt-out applied), never localStorage raw.
      note: () => getAttributionNote(),
    };
    if (opts.app === 'lamirage-in') trackPageview(window.location.pathname, opts.notFound?.() ? { nf: 1 } : undefined);
  });
}

export function trackPageview(rawPath: string, extra?: { nf?: 1 }): void {
  guard(() => {
    if (!S.on) return;
    // wid belongs to the raw path it was set on. An SPA move elsewhere drops it BEFORE this
    // pageview (the old page's own engagement, flushed just before, still carried it).
    if (S.wid !== null && rawPath !== S.widRawPath) { S.wid = null; S.widRawPath = null; }
    S.rawPath = rawPath;
    S.path = pathTemplate(rawPath);
    S.visibleMs = 0;
    S.maxScroll = 0;
    S.visibleSince = visible() ? Date.now() : 0;
    S.nf = !!extra?.nf;
    const sess = touchSession();
    const body: Record<string, unknown> = { t: 'pv', p: S.path, sw: core.screenBucket(window.innerWidth) };
    if (S.firstPv && S.landing) {
      if (S.landing.referrerHost) body.r = S.landing.referrerHost;
      if (S.landing.utm) body.u = S.landing.utm;
      if (S.landing.clickKind) body.k = S.landing.clickKind;
      const ls = kv('local');
      if (ls) core.updateNotes(ls, S.landing, sess.isNew, new Date());
    }
    S.firstPv = false;
    if (S.ret) body.ret = true;
    if (S.wid) body.wid = S.wid;
    if (extra?.nf) body.nf = 1;
    if ((navigator as { webdriver?: boolean }).webdriver) body.wd = 1;
    send(body);
  });
}

/** Engagement for the CURRENT path. Called on hidden/pagehide, and by the SPA hook before a route change. */
export function flushEngagement(): void {
  guard(() => {
    if (!S.on) return;
    const ms = S.visibleMs + (S.visibleSince ? Date.now() - S.visibleSince : 0);
    S.visibleMs = 0;
    S.visibleSince = visible() ? Date.now() : 0;
    if (ms < 500) return;
    send({
      t: 'en', p: S.path, d: Math.min(Math.round(ms), 1_800_000), sc: Math.max(0, Math.min(100, Math.round(S.maxScroll))),
      ...(S.wid ? { wid: S.wid } : {}), ...(S.nf ? { nf: 1 } : {}),
    });
  });
}

export function trackEvent<N extends EventName>(name: N, props: EventPropsMap[N]): void {
  guard(() => {
    if (!S.on) return;
    const allowed = EVENT_PROP_KEYS[name];
    if (!allowed) return;
    touchSession();
    const pr: Record<string, unknown> = {};
    for (const k of allowed) {
      const v = (props as Record<string, unknown> | undefined)?.[k];
      if (v !== undefined) pr[k] = v;
    }
    send({ t: 'ev', p: S.path, n: name, ...(Object.keys(pr).length ? { pr } : {}), ...(S.wid ? { wid: S.wid } : {}) });
  });
}

/** Sets (or clears, with null) the wedding id for the CURRENT page only — see trackPageview. */
export function setAnalyticsWedding(id: number | null | undefined): void {
  guard(() => {
    S.wid = Number.isInteger(id) && (id as number) > 0 ? (id as number) : null;
    S.widRawPath = S.wid !== null ? S.rawPath : null;
  });
}

/**
 * lamirage.co NotFound route: the pageview is already out, so the flag rides on this page's
 * engagement. Relies on ordering: useAnalytics sends the pageview in a LAYOUT effect, NotFound calls
 * this from a PASSIVE effect, which React always runs after every layout effect of the commit.
 */
export function markNotFound(): void { S.nf = true; }

/** The ft/lt note for enquire/call/book requests (R2). undefined when opted out or nothing stored. */
export function getAttributionNote(): core.AttributionNoteOut | undefined {
  try {
    if (!S.on) return undefined;
    const ls = kv('local');
    return ls ? core.readNote(ls, new Date()) : undefined;
  } catch {
    return undefined;
  }
}
